import React from 'react'

/*
 * `ComponentType<any>` is what React's own LazyExoticComponent is declared
 * against, so matching it here is what makes this wrapper assignable wherever a
 * lazy component is accepted. Narrowing it breaks that assignability.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyComponent = React.ComponentType<any>
/* eslint-enable @typescript-eslint/no-explicit-any */

/** A lazily-loaded component that can also be fetched ahead of time. */
export type PreloadableComponent<T extends AnyComponent> = React.LazyExoticComponent<T> & {
  preload: () => Promise<{ default: T }>
}

/**
 * React.lazy that can also be fetched ahead of time.
 *
 * Code splitting means a route's chunk is only requested when the user
 * navigates, so the click itself pays for the download. Keeping a handle on the
 * importer lets us start that download earlier — while the browser is idle, or
 * the moment a user shows intent — so navigation feels instant.
 *
 * The bundler caches the import promise, so calling `preload()` and then
 * rendering the component resolves the same module and never double-fetches.
 */
export function lazyWithPreload<T extends AnyComponent>(
  importer: () => Promise<{ default: T }>
): PreloadableComponent<T> {
  const Component = React.lazy(importer) as PreloadableComponent<T>
  Component.preload = importer
  return Component
}

/**
 * Run preloads once the browser has spare time.
 *
 * Deliberately after `load` and behind requestIdleCallback: prefetching must
 * never compete with the page the user is actually looking at. Failures are
 * swallowed — a missed prefetch just becomes a normal lazy load later.
 */
export function preloadWhenIdle(preloads: Array<() => Promise<unknown>>): void {
  if (typeof window === 'undefined') return

  const run = () => {
    for (const preload of preloads) {
      try {
        void preload().catch(() => {})
      } catch {
        /* a failed prefetch is never fatal */
      }
    }
  }

  const schedule = () => {
    // Read the function off window rather than narrowing window itself — the DOM
    // lib declares requestIdleCallback as always present, which would make the
    // Safari fallback branch unreachable to the type checker.
    const idle = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number
      }
    ).requestIdleCallback

    if (typeof idle === 'function') idle.call(window, run, { timeout: 4000 })
    else window.setTimeout(run, 2500)
  }

  if (document.readyState === 'complete') schedule()
  else window.addEventListener('load', schedule, { once: true })
}
